import styled from "styled-components";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 640px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
  justify-content: center;
`;

const Tab = styled.a`
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Map + Contact ── */
const ContentSection = styled.section`
  padding: clamp(32px, 4vw, 48px) 0 clamp(80px, 10vw, 120px);
`;

const MapFrame = styled.div`
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  height: clamp(360px, 50vw, 520px);
  box-shadow: 0 24px 50px -28px rgba(15, 23, 42, 0.18);

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: #fafbff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 24px 26px;
  display: flex;
  align-items: center;
  gap: 18px;

  .icon {
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: linear-gradient(135deg, #6366f1, #7c3aed);
    color: #ffffff;
  }

  .label {
    font-size: 11.5px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 0 0 4px;
  }

  .value {
    font-size: 15px;
    color: #0f172a;
    font-weight: 500;
    margin: 0;
    line-height: 1.55;

    a {
      color: inherit;
      text-decoration: none;

      &:hover {
        color: #6366f1;
      }
    }
  }
`;

const DirectionsWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const DirectionsBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: #1e293b;
  }
`;

/* ── Icons ── */
const PinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const MailIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 6-10 7L2 6" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const TABS = [
  { key: "greetings", label: "Greetings", href: "/aboutUs/greetings" },
  { key: "vision", label: "Vision", href: "/aboutUs/vision" },
  { key: "history", label: "History", href: "/aboutUs/history" },
  { key: "map", label: "Map", href: "/aboutUs/map" },
];

export function Map() {
  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Company</Eyebrow>
          <HeroTitle>Find us</HeroTitle>
          <HeroLead>
            Visit our office at I-PLEX Gwangju — or drop us a note any time.
          </HeroLead>
          <Tabs>
            {TABS.map((t) => (
              <Tab key={t.key} href={t.href} $active={t.key === "map"}>
                {t.label}
              </Tab>
            ))}
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <MapFrame>
            <iframe
              title="Youngand office location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d839.4793055301286!2d126.92574643885229!3d35.14854304942375!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35718c9098cd6191%3A0x704e139b24be464!2zSS1QTEVYIOq0keyjvA!5e0!3m2!1sko!2skr!4v1735629442001!5m2!1sko!2skr"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </MapFrame>

          <ContactGrid>
            <ContactCard>
              <div className="icon">
                <PinIcon />
              </div>
              <div>
                <p className="label">Address</p>
                <p className="value">
                  Room 108, I-PLEX Gwangju, 150 Donggyecheon-ro, Dong-gu,
                  Gwangju, Republic of Korea
                </p>
              </div>
            </ContactCard>
            <ContactCard>
              <div className="icon">
                <MailIcon />
              </div>
              <div>
                <p className="label">Email</p>
                <p className="value">
                  <a href="mailto:info@youngandx.com">info@youngandx.com</a>
                </p>
              </div>
            </ContactCard>
          </ContactGrid>

          <DirectionsWrap>
            <DirectionsBtn
              href="https://www.google.com/maps/place/I-PLEX+%EA%B4%91%EC%A3%BC/@35.1485431,126.9257464,17z"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
              <ArrowIcon />
            </DirectionsBtn>
          </DirectionsWrap>
        </Container>
      </ContentSection>
    </Page>
  );
}
